"use client";

import { useMemo } from "react";
import {
  Button,
  Flex,
  RadioGroup,
  Separator,
  Text,
  TextField,
} from "@radix-ui/themes";
import FontAwesome from "../../components/utilities/FontAwesome";
import { useT } from "../../components/utilities/TranslatedText";
import styles from "./PageIndexPage.module.scss";
import { PublishedFilterValue } from "./PageIndexPageClient";

const ALL_LETTERS = [
  "#",
  ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)),
];

type PageIndexSidebarFiltersProps = {
  idPrefix: string;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  showPublishedFilter: boolean;
  publishedFilter: PublishedFilterValue;
  onPublishedChange: (value: PublishedFilterValue) => void;
  letters: string[];
  activeLetter: string | null;
  onLetterClick: (letter: string) => void;
  disabled: boolean;
};

export default function PageIndexSidebarFilters({
  idPrefix,
  searchTerm,
  onSearchChange,
  showPublishedFilter,
  publishedFilter,
  onPublishedChange,
  letters,
  activeLetter,
  onLetterClick,
  disabled,
}: PageIndexSidebarFiltersProps) {
  const t = useT();
  const availableSet = useMemo(() => new Set(letters), [letters]);

  return (
    <Flex direction="column" gap="4">
      <Flex direction="column" gap="1">
        <Text
          as="label"
          htmlFor={`${idPrefix}-page-index-search`}
          size="1"
          weight="bold"
          color="gray"
        >
          {t("Search")}
        </Text>
        <TextField.Root
          id={`${idPrefix}-page-index-search`}
          placeholder={t("Search pages...")}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          size="2"
        >
          <TextField.Slot>
            <FontAwesome prefix="fas" name="fa-search" ariaHidden />
          </TextField.Slot>
        </TextField.Root>
      </Flex>

      {showPublishedFilter && (
        <>
          <Separator size="4" />
          <Flex direction="column" gap="2">
            <Text size="1" weight="bold" color="gray">
              {t("Published")}
            </Text>
            <RadioGroup.Root
              value={publishedFilter}
              onValueChange={(value) =>
                onPublishedChange(value as PublishedFilterValue)
              }
            >
              <Flex direction="column" gap="2">
                <RadioGroup.Item value="all" disabled={disabled}>
                  {t("All")}
                </RadioGroup.Item>
                <RadioGroup.Item value="published" disabled={disabled}>
                  {t("Published")}
                </RadioGroup.Item>
                <RadioGroup.Item value="unpublished" disabled={disabled}>
                  {t("Unpublished")}
                </RadioGroup.Item>
              </Flex>
            </RadioGroup.Root>
          </Flex>
        </>
      )}

      <Separator size="4" />
      <Flex
        direction="column"
        gap="2"
        role="group"
        aria-label={t("Starts with")}
      >
        <Text size="1" weight="bold" color="gray">
          {t("Starts with")}
        </Text>
        <Flex asChild wrap="wrap" gap="1">
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            <li style={{ listStyle: "none", flexBasis: "15%" }}>
              <Button
                variant={!activeLetter ? "solid" : "surface"}
                size="2"
                onClick={() => onLetterClick("")}
                disabled={disabled}
                aria-label={t("Show all pages")}
                className={styles.letterButton}
              >
                {t("All")}
              </Button>
            </li>
            {ALL_LETTERS.map((letter) => {
              const isAvailable = availableSet.has(letter);
              return (
                <li
                  key={letter}
                  style={{ listStyle: "none", flexBasis: "15%" }}
                >
                  <Button
                    variant={activeLetter === letter ? "solid" : "surface"}
                    color={!isAvailable ? "gray" : undefined}
                    highContrast={isAvailable}
                    size="2"
                    onClick={() => onLetterClick(letter)}
                    disabled={disabled || !isAvailable}
                    aria-label={
                      letter === "#"
                        ? t("Jump to numbers and symbols")
                        : `${t("Jump to letter")} ${letter}`
                    }
                    className={styles.letterButton}
                  >
                    {letter}
                  </Button>
                </li>
              );
            })}
          </ul>
        </Flex>
      </Flex>
    </Flex>
  );
}
